import { useEffect, useState } from 'react';
import { X, AlertTriangle, Loader2, Check, Sparkles } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

/**
 * Payer Cleanup panel — iter 231.
 *
 * Detects near-duplicate live payers via GET /api/vault/payers/duplicate-clusters
 * and lets a super_user / admin pick a canonical member and merge the rest
 * into it via POST /api/vault/payers/merge. The merge re-points every
 * contract rate, enrollment, credentialing, and child-payer relationship
 * at the canonical member so downstream reports stay intact.
 */
export default function PayerCleanupPanel({ onClose }) {
  const [clusters, setClusters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyClusterId, setBusyClusterId] = useState(null);
  const [errText, setErrText] = useState('');
  // Per-cluster staged state — which member is canonical, hard_delete flag.
  const [staged, setStaged] = useState({});

  const load = async () => {
    setLoading(true);
    setErrText('');
    try {
      const r = await fetch(`${API_BASE}/api/vault/payers/duplicate-clusters`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setClusters(d.clusters || []);
    } catch (e) {
      setErrText(String(e).slice(0, 200));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setKeep = (canonical, payerId) => {
    setStaged(s => ({ ...s, [canonical]: { ...(s[canonical] || {}), keep: payerId } }));
  };
  const toggleHardDelete = (canonical) => {
    setStaged(s => ({
      ...s,
      [canonical]: { ...(s[canonical] || {}), hard: !(s[canonical]?.hard) },
    }));
  };

  const runMerge = async (cluster) => {
    const stage = staged[cluster.canonical] || {};
    const keep = stage.keep || cluster.members[0]?.payer_id;
    if (!keep) return;
    const merge = cluster.members.filter(m => m.payer_id !== keep).map(m => m.payer_id);
    if (merge.length === 0) return;
    const keepName = cluster.members.find(m => m.payer_id === keep)?.name;
    const mergeNames = cluster.members.filter(m => m.payer_id !== keep).map(m => m.name).join(', ');
    const verb = stage.hard ? 'HARD-DELETE' : 'soft-delete';
    if (!window.confirm(
      `Merge into "${keepName}":\n  ${mergeNames}\n\n` +
      `Action: ${verb} the merged rows, re-point every contract rate / enrollment / credentialing at "${keepName}".\n\n` +
      `Continue?`
    )) return;
    setBusyClusterId(cluster.canonical);
    setErrText('');
    try {
      const r = await fetch(`${API_BASE}/api/vault/payers/merge`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keep_payer_id: keep,
          merge_payer_ids: merge,
          hard_delete: !!stage.hard,
        }),
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.detail || `HTTP ${r.status}`);
      await load();
    } catch (e) {
      setErrText(`Merge failed: ${String(e).slice(0, 200)}`);
    } finally {
      setBusyClusterId(null);
    }
  };

  return (
    <div
      data-testid="payer-cleanup-panel"
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(45, 40, 51, 0.55)' }}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden"
        style={{ background: '#FBF8F4', border: '1px solid #E8E5EC' }}
      >
        {/* Header */}
        <div
          className="px-5 py-3.5 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #685B72 0%, #685B72 100%)', color: '#B08A3A' }}
        >
          <div className="flex items-center gap-2.5">
            <div className="rounded-full p-1.5" style={{ background: '#B08A3A22' }}>
              <Sparkles size={14} />
            </div>
            <div>
              <div className="font-serif text-base leading-none">Payer Cleanup</div>
              <div className="text-[11px] opacity-80 mt-1">
                Auto-detect near-duplicate payers still live in the vault
              </div>
            </div>
          </div>
          <button
            data-testid="payer-cleanup-close"
            onClick={onClose}
            className="p-1.5 rounded-md opacity-80 hover:opacity-100 hover:bg-white/10"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {loading && (
            <div className="flex items-center justify-center py-14 gap-2 text-sm" style={{ color: '#685B72' }}>
              <Loader2 size={16} className="animate-spin" /> Scanning payers…
            </div>
          )}
          {errText && (
            <div
              data-testid="payer-cleanup-error"
              className="flex items-start gap-2 rounded-lg p-3 text-sm"
              style={{ background: '#FBE7E4', color: '#7A2E1C', border: '1px solid #E9B5AC' }}
            >
              <AlertTriangle size={16} className="mt-0.5 shrink-0" />
              <div>{errText}</div>
            </div>
          )}
          {!loading && clusters.length === 0 && !errText && (
            <div
              data-testid="payer-cleanup-empty"
              className="flex flex-col items-center justify-center py-14 gap-2"
              style={{ color: '#685B72' }}
            >
              <div className="rounded-full p-2.5" style={{ background: '#DDECD3' }}>
                <Check size={18} style={{ color: '#3E6B26' }} />
              </div>
              <div className="font-serif text-lg">No duplicate payers detected</div>
              <div className="text-xs opacity-70">
                Every live payer has a unique canonical name — nothing to clean up.
              </div>
            </div>
          )}

          {!loading && clusters.map(cluster => {
            const stage = staged[cluster.canonical] || {};
            const keepId = stage.keep || cluster.members[0]?.payer_id;
            const busy = busyClusterId === cluster.canonical;
            return (
              <div
                key={cluster.canonical}
                data-testid={`payer-cluster-${cluster.canonical}`}
                className="rounded-xl p-4 space-y-3"
                style={{ background: 'white', border: '1px solid #E8E5EC' }}
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] uppercase tracking-wide opacity-60" style={{ color: '#685B72' }}>
                      Cluster · canonical "{cluster.canonical}"
                    </div>
                    <div className="font-serif text-base mt-0.5" style={{ color: '#2D2833' }}>
                      {cluster.size} similar payers
                    </div>
                  </div>
                  <label className="flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: '#685B72' }}>
                    <input
                      data-testid={`payer-hard-delete-${cluster.canonical}`}
                      type="checkbox"
                      checked={!!stage.hard}
                      onChange={() => toggleHardDelete(cluster.canonical)}
                      className="accent-[#685B72]"
                    />
                    hard-delete (irreversible)
                  </label>
                </div>
                <div className="space-y-1.5">
                  {cluster.members.map(m => {
                    const isKeep = m.payer_id === keepId;
                    return (
                      <label
                        key={m.payer_id}
                        className="flex items-center gap-3 p-2.5 rounded-lg text-sm cursor-pointer transition"
                        style={{
                          background: isKeep ? '#F1EDF5' : '#FBF8F4',
                          border: `1px solid ${isKeep ? '#BAA9CA' : '#E8E5EC'}`,
                        }}
                      >
                        <input
                          data-testid={`payer-keep-${cluster.canonical}-${m.payer_id.slice(0,8)}`}
                          type="radio"
                          name={`keep-${cluster.canonical}`}
                          checked={isKeep}
                          onChange={() => setKeep(cluster.canonical, m.payer_id)}
                          className="accent-[#685B72]"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="font-medium truncate" style={{ color: '#2D2833' }}>
                            {m.name}
                            {isKeep && (
                              <span
                                className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full"
                                style={{ background: '#DDECD3', color: '#3E6B26' }}
                              >
                                KEEP
                              </span>
                            )}
                          </div>
                          <div className="text-[11px] opacity-70 mt-0.5" style={{ color: '#685B72' }}>
                            id={m.payer_id.slice(0, 8)} · rates={m.contract_rate_count} · children={m.child_count}
                            {m.parent_payer_id && ' · has parent'}
                          </div>
                        </div>
                      </label>
                    );
                  })}
                </div>
                <div className="flex items-center justify-end gap-2 pt-1">
                  <button
                    data-testid={`payer-merge-btn-${cluster.canonical}`}
                    onClick={() => runMerge(cluster)}
                    disabled={busy}
                    className="px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed transition"
                    style={{ background: '#685B72', color: '#FFFFFF' }}
                  >
                    {busy ? (
                      <span className="flex items-center gap-1.5"><Loader2 size={13} className="animate-spin" /> Merging…</span>
                    ) : (
                      'Merge into KEEP'
                    )}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
